import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSite } from '@/services/siteData';
import { IcoInstagram, IcoCaret } from './icons';

// The Client & Trade Portal is part of this app (the header Login button routes
// to /login). An external URL can still override it via VITE_PORTAL_URL.
const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || '';

/** Reveal/collapse the full header on scroll (matches the live didiers.co.uk
 *  behaviour): scrolling down collapses to just the nav bar, scrolling up (or
 *  returning to the top) slides the full header back into view. */
function useCollapseOnScroll() {
  const [collapsed, setCollapsed] = useState(false);
  const collapsedRef = useRef(false);
  const lastY = useRef(0);
  const lastToggle = useRef(0);

  useEffect(() => {
    lastY.current = window.scrollY;
    let ticking = false;

    // Hysteresis + cooldown to stop the erratic flip-flop near the threshold:
    // collapsing a sticky header reflows the page, which nudges the scroll and
    // can re-trigger the opposite direction. Separate thresholds plus a short
    // post-toggle lock let the layout settle before the next decision.
    const COLLAPSE_AT = 150; // only collapse once the full header has scrolled away
    const EXPAND_AT = 80; // always show the full header near the top
    const DELTA = 10; // min movement before a scroll counts as a direction
    const COOLDOWN = 280; // ms — ignore further toggles right after one (let reflow settle)

    const setState = (next: boolean) => {
      if (collapsedRef.current === next) return;
      collapsedRef.current = next;
      lastToggle.current = performance.now();
      setCollapsed(next);
    };

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const prev = lastY.current;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

      // Near the bottom, freeze — collapsing shrinks the document and would clamp scroll.
      if (maxScroll - y < 160) {
        lastY.current = y;
        return;
      }
      // Within the cooldown window, only track position; don't toggle.
      if (performance.now() - lastToggle.current < COOLDOWN) {
        lastY.current = y;
        return;
      }

      if (y <= EXPAND_AT) {
        setState(false);
      } else if (y > prev + DELTA && y > COLLAPSE_AT) {
        setState(true); // scrolling down, past the header
      } else if (y < prev - DELTA) {
        setState(false); // scrolling up
      }
      lastY.current = y;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return collapsed;
}

export function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { getRanges, content } = useSite();
  const orderRanges = getRanges('order');
  const portfolioRanges = getRanges('portfolio');
  const isActive = (prefix: string) => pathname.startsWith(prefix);
  const collapsed = useCollapseOnScroll();

  return (
    <header className={'site-header' + (collapsed ? ' collapsed' : '')}>
      <div className="header-inner">
        <div className="header-top">
          <div className="brand">
            <button onClick={() => navigate('/')} aria-label="Didier's Patisserie — home">
              <img className="brand-logo" src={content.branding?.headerLogo || '/assets/didiers-logo.svg'} alt="Didier's Patisserie — by Didier Merveilleux" />
            </button>
          </div>
          <div className="header-util">
            <a className="ig-link" href={content.contact.instagramUrl} target="_blank" rel="noopener" title="Follow on Instagram">
              <IcoInstagram />
            </a>
            {PORTAL_URL ? (
              <a className="nav-login" href={PORTAL_URL} title="Client &amp; Trade Portal">Login</a>
            ) : (
              <Link className="nav-login" to="/login" title="Client &amp; Trade Portal">Login</Link>
            )}
            <button className="contact-btn" onClick={() => navigate('/contact')}>Contact Us Today</button>
          </div>
        </div>

        <nav className="nav-row">
          <div className="nav-group">
            <div className="nav-item">
              <button className={'nav-link' + (isActive('/order') ? ' active' : '')}>
                Available to Order <IcoCaret />
              </button>
              <div className="dropdown">
                {orderRanges.map((r) => (
                  <Link key={r.key} to={`/order/${r.slug}`}>{r.label}</Link>
                ))}
              </div>
            </div>
            <div className="nav-item">
              <button className={'nav-link' + (isActive('/portfolio') ? ' active' : '')}>
                Portfolio <IcoCaret />
              </button>
              <div className="dropdown">
                {portfolioRanges.map((r) => (
                  <Link key={r.key} to={`/portfolio/${r.slug}`}>{r.label}</Link>
                ))}
              </div>
            </div>
            <div className="nav-item">
              <button
                className={'nav-link' + (pathname === '/innovation' ? ' active' : '')}
                onClick={() => navigate('/innovation')}
              >
                Innovation
              </button>
            </div>
          </div>
          <div className="nav-group">
            <button
              className={'nav-link' + (pathname === '/about' ? ' active' : '')}
              onClick={() => navigate('/about')}
            >
              Our Story
            </button>
            <button
              className={'nav-link' + (pathname === '/csr' ? ' active' : '')}
              onClick={() => navigate('/csr')}
            >
              CSR / Sustainability
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}
